import { useEffect } from 'react'
import { Modal } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { observer } from 'mobx-react-lite'
import { Button } from 'blaze-ui/Button'
import { Close } from 'blaze-ui/icons'
import { InstagramReelPreview } from 'components/Previews/InstagramReelPreview/InstagramReelPreview'
import { trackEventInHeap, HeapEventType } from 'utils/heapTracking'
import Styles from '../VisualStylesForExistingUsersModal.module.scss'
import { useCreditUsage } from 'hooks/useCreditUsage'

const VIDEO_PREVIEW_URL =
  'https://res.cloudinary.com/almanac/video/upload/v1767834736/blaze_assets/content_preferences/smiling_model_ni1ccz.mp4'

interface AnimatedImagesOptInStepProps {
  onOptIn: (optIn: boolean) => void
  onBack: () => void
  onClose: () => void
}

export const AnimatedImagesOptInStep = observer(({ onOptIn, onBack, onClose }: AnimatedImagesOptInStepProps) => {
  const { usageCosts } = useCreditUsage()
  const animatedImagesCost = usageCosts?.video_credits_cost || 0

  useEffect(() => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'view-animated-images-step',
    })
  }, [])

  const handleOptIn = () => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'complete-animated-images-step',
      animated_images_opt_in: true,
    })
    onOptIn(true)
  }

  const handleSkip = () => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'complete-animated-images-step',
      animated_images_opt_in: false,
    })
    onOptIn(false)
  }

  return (
    <Modal.Content className={Styles.animatedImagesContent}>
      <div className={Styles.animatedImagesStep}>
        <div className={Styles.animatedImagesLeftPanel}>
          <div className={Styles.animatedImagesLeftPanelContent}>
            <div className={Styles.animatedImagesHeader}>
              <Heading level={1}>Transform your photos into engaging AI videos</Heading>
              <Text color="var(--dark-40)" variant="primary">
                Blaze automatically transforms your Brand Kit photos into professional videos using AI
              </Text>
            </div>

            <div className={Styles.featureBox}>
              <div className={Styles.featureItem}>
                <div className={Styles.featureContent}>
                  <Heading level={5} color="var(--dark-90)">
                    📹 More content, zero effort
                  </Heading>
                  <Text variant="secondary" color="var(--dark-40)">
                    Blaze picks your best photos and turns them into videos automatically
                  </Text>
                </div>
              </div>

              <div className={Styles.featureItem}>
                <div className={Styles.featureContent}>
                  <Heading level={5} color="var(--dark-90)">
                    🎯 Scroll-stopping quality
                  </Heading>
                  <Text variant="secondary" color="var(--dark-40)">
                    Professional camera movements, effects, and music in every 8-second video
                  </Text>
                </div>
              </div>

              <div className={Styles.featureItem}>
                <div className={Styles.featureContent}>
                  <Heading level={5} color="var(--dark-90)">
                    ✅ Always ready to post
                  </Heading>
                  <Text variant="secondary" color="var(--dark-40)">
                    Videos appear in your calendar—review, edit, or publish instantly
                  </Text>
                </div>
              </div>

              <div className={Styles.optInButtonContainer}>
                <Button variant="primary" size="xl" onPress={handleOptIn}>
                  Yes, turn my images into AI videos
                </Button>
                <Text variant="label" color="var(--dark-40)">
                  AI videos use {animatedImagesCost} credits each. Add credits on the next screen.
                </Text>
              </div>
            </div>

            <div className={Styles.animatedImagesActions}>
              <Button variant="secondary" size="lg" onPress={handleSkip}>
                Continue without AI videos
              </Button>
              <Text variant="label" color="var(--dark-40)">
                Not sure? You can always disable AI videos later in settings.
              </Text>
            </div>
          </div>

          <div className={Styles.leftPanelFooter}>
            <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
              Back
            </Button>
          </div>
        </div>

        <div className={Styles.animatedImagesRightPanel}>
          <Button size="sm" variant="tertiary" onPress={onClose} className={Styles.stepCloseButton}>
            <Close color="var(--light-100)" />
          </Button>
          <div className={Styles.reelPreviewContainer}>
            <InstagramReelPreview
              mediaObjects={[
                {
                  id: 1,
                  source_url: VIDEO_PREVIEW_URL,
                  thumbnail_url: VIDEO_PREVIEW_URL,
                  mime_type: 'video/mp4',
                  position: 1,
                },
              ]}
              content="Great style gets better with time. Our loyalty..."
              showVideoControls={false}
              isActive
            />
          </div>
        </div>
      </div>
    </Modal.Content>
  )
})
